import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import cx from 'clsx';

import { useTranslation } from 'react-i18next';
import { CUSTOMER_INFO_TEST_IDS, STATE_LIST } from './const';
import {
  AddressFields,
  Customer,
  WalletResponse,
} from 'services/checkout-service/models';
import {
  validateEmail,
  getCountryFromLocale,
  validateFirstName,
  validateLastName,
  validateBusinessName,
  validatePhoneNumber,
  lengthCheckRule,
  formatValueAsPhone,
  validateZipCode,
} from 'utils/Regex';
import {
  ErrorInputState,
  CustomerInfoFields,
  NameInputState,
  AddressInputState,
  EmailInputState,
} from '../CloverPaymentSection';
import { Dropdown, OptionValue } from 'utils/Dropdown';

import styles from './styles.module.scss';

type CustomerInfoProps = {
  customer?: Customer;
  emailInputState: EmailInputState;
  setEmailInputState: Dispatch<SetStateAction<EmailInputState>>;
  nameInputState: NameInputState;
  setNameInputState: Dispatch<SetStateAction<NameInputState>>;
  businessNameInputState: ErrorInputState;
  setBusinessNameInputState: Dispatch<SetStateAction<ErrorInputState>>;
  phoneNumberInputState: ErrorInputState;
  setPhoneNumberInputState: Dispatch<SetStateAction<ErrorInputState>>;
  addressInputState: AddressInputState;
  setAddressInputState: Dispatch<SetStateAction<AddressInputState>>;
  setCustomerInfo: Dispatch<SetStateAction<Partial<Customer> | undefined>>;
  customerFields: CustomerInfoFields;
  isCustomerInfoFeatureEnabled: boolean;
};

export const getAddressInfo = (customer?: Partial<Customer>, locale?: string, wallet?: WalletResponse) => {
  if (wallet?.customer?.address?.elements?.length) return wallet.customer.address.elements[0];

  const addressInfo = getAddress(customer);
  const obj: AddressFields = {
    address1: addressInfo?.address1 || '',
    address2: addressInfo?.address2 || '',
    state: addressInfo?.state || '',
    city: addressInfo?.city || '',
    zip: addressInfo?.zip || '',
    country: getCountryFromLocale(locale || ''),
  };

  return obj;
};

export const getCustomerEmail = (
  customer?: Partial<Customer>,
  wallet?: WalletResponse
) => {
  if (wallet) {
    if (wallet.customer?.email) return wallet.customer?.email;
    console.error('Failed to get email from wallet');
  }

  return (
    customer?.emailAddresses?.elements[0]?.emailAddress ||
    customer?.email?.elements[0]?.emailAddress ||
    ''
  );
};

export const getPhoneNumber = (customer?: Partial<Customer>) => {
  return (
    customer?.phoneNumbers?.elements[0]?.phoneNumber ||
    customer?.phoneNumber?.elements[0]?.phoneNumber ||
    ''
  );
};

export const getAddress = (customer?: Partial<Customer>) => {
  return customer?.addresses?.elements[0] || customer?.address?.elements[0];
};

export const getBusinessName = (customer?: Partial<Customer>) => {
  return customer?.metadata?.businessName || '';
};

export const getNames = (customer?: Partial<Customer>, wallet?: WalletResponse) => {
  if (wallet?.customer) {
    const walletCustomer = wallet.customer;
    const firstName = walletCustomer.billingInfo?.billingAddress?.name || walletCustomer?.firstName || '';
    const lastName = walletCustomer?.lastName || '';

    if (firstName || lastName) {
      return {
        firstName,
        lastName,
      };
    }
  }

  return {
    firstName: customer?.firstName,
    lastName: customer?.lastName,
  };
};

const CustomerInfo: FC<CustomerInfoProps> = (props) => {
  const {
    customer,
    emailInputState,
    setEmailInputState,
    nameInputState,
    setNameInputState,
    businessNameInputState,
    setBusinessNameInputState,
    phoneNumberInputState,
    setPhoneNumberInputState,
    addressInputState,
    setAddressInputState,
    setCustomerInfo,
    customerFields,
    isCustomerInfoFeatureEnabled,
  } = props;

  const comparePrevEmail = (mail: string) => {
    // compare with email id rejected from API
    return mail === emailInputState.invalidEmail;
  };

  const { t } = useTranslation();
  const { firstName = '', lastName = '' } = customer || {};
  const initialEmailAddress = getCustomerEmail(customer);
  const initialPhoneNumber = getPhoneNumber(customer);
  const initialAddress = getAddress(customer);
  const initialBusinessName = getBusinessName(customer);
  const [firstNameValue, setFirstName] = useState(firstName);
  const [lastNameValue, setLastName] = useState(lastName);
  const [emailAddressValue, setEmail] = useState(initialEmailAddress);
  const [businessNameValue, setBusinessName] = useState(initialBusinessName);
  const [address1Value, setAddress1Value] = useState(
    initialAddress?.address1 || ''
  );
  const [address2Value, setAddress2Value] = useState(
    initialAddress?.address2 || ''
  );
  const [cityValue, setCity] = useState(initialAddress?.city || '');
  const [stateValue, setState] = useState(initialAddress?.state || '');
  const [zipValue, setZip] = useState(initialAddress?.zip || '');
  const [phoneValue, setPhoneValue] = useState(initialPhoneNumber);
  useEffect(() => {
    setCustomerInfo(() => ({
      firstName: firstNameValue,
      lastName: lastNameValue,
      metadata: {
        businessName: businessNameValue,
      },
      address: {
        elements: [
          {
            address1: address1Value,
            address2: address2Value,
            city: cityValue,
            state: stateValue,
            zip: zipValue,
          },
        ],
      },
      phoneNumber: {
        elements: [
          {
            phoneNumber: phoneValue,
          },
        ],
      },
      email: {
        elements: [
          {
            emailAddress: emailAddressValue,
          },
        ],
      },
    }));
  }, [
    firstNameValue,
    lastNameValue,
    emailAddressValue,
    businessNameValue,
    address1Value,
    address2Value,
    cityValue,
    stateValue,
    zipValue,
    phoneValue,
    setCustomerInfo,
  ]);

  const {
    ECOMM_CHECKOUT_CUSTOMER_INFO_BUSINESS_NAME_ENABLED: businessNameEnabled,
    ECOMM_CHECKOUT_CUSTOMER_INFO_BUSINESS_NAME_REQUIRED: businessNameRequired,
    ECOMM_CHECKOUT_CUSTOMER_INFO_PHONE_NUMBER_ENABLED: phoneNumberEnabled,
    ECOMM_CHECKOUT_CUSTOMER_INFO_PHONE_NUMBER_REQUIRED: phoneNumberRequired,
    ECOMM_CHECKOUT_CUSTOMER_INFO_BILLING_ADDRESS_ENABLED: billingAddrEnabled,
    ECOMM_CHECKOUT_CUSTOMER_INFO_BILLING_ADDRESS_REQUIRED: billingAddrRequired,
  } = customerFields || {};

  const isBusinessNameEnabled = businessNameEnabled === '1';
  const isBusinessNameRequired = businessNameRequired === '1';
  const isPhoneNumberEnabled = phoneNumberEnabled === '1';
  const isPhoneNumberRequired = phoneNumberRequired === '1';
  const isBillingAddrEnabled = billingAddrEnabled === '1';
  const isBillingAddrRequired = billingAddrRequired === '1';

  const handleStateChange = (selection: OptionValue) => {
    setState(selection.label);
  };

  const stateObj = STATE_LIST.find(
    (option) => option.value === stateValue || option.label === stateValue
  );

  const handleLabel = (
    <>
      <label className={styles.button}>{stateObj && stateObj.label}</label>
      <img
        className={styles.displayImg}
        src={require('./images/downArrow.png')}
        width={20}
        alt=""
      />
    </>
  );

  const getLabel = (fieldName: string, required?: boolean) => t(`customerInformation.placeHolder.${fieldName}`) + (required ? '*' : '');
  return (
    <div className={styles.wrapper}>
      {
        <div className={styles.row}>
          <input
            data-testid={CUSTOMER_INFO_TEST_IDS.inputFirstName}
            className={cx(
              styles.input,
              styles.dualInput,
              nameInputState.firstName.hasError && styles.error
            )}
            placeholder={getLabel('firstName', isCustomerInfoFeatureEnabled)}
            name="firstName"
            type="text"
            value={firstNameValue}
            readOnly={!!firstName}
            autoComplete="given-name"
            onChange={(e) => {
              setFirstName(e.target.value);
              setNameInputState({
                ...nameInputState,
                firstName: {
                  hasError: false,
                  isTouched: true,
                },
              });
            }}
            onBlur={(e) => {
              setNameInputState({
                ...nameInputState,
                firstName: {
                  isTouched: true,
                  hasError:
                    isCustomerInfoFeatureEnabled &&
                    !firstName &&
                    !validateFirstName(e.target.value),
                },
              });
            }}
          />
          <input
            data-testid={CUSTOMER_INFO_TEST_IDS.inputLastName}
            className={cx(
              styles.input,
              nameInputState.lastName.hasError && styles.error
            )}
            placeholder={getLabel('lastName', isCustomerInfoFeatureEnabled)}
            name="lastName"
            type="text"
            value={lastNameValue}
            readOnly={!!lastName}
            autoComplete="family-name"
            onChange={(e) => {
              setLastName(e.target.value);
              setNameInputState({
                ...nameInputState,
                lastName: {
                  hasError: false,
                  isTouched: true,
                },
              });
            }}
            onBlur={(e) => {
              setNameInputState({
                ...nameInputState,
                lastName: {
                  isTouched: true,
                  hasError:
                    isCustomerInfoFeatureEnabled &&
                    !lastName &&
                    !validateLastName(e.target.value),
                },
              });
            }}
          />
        </div>
      }
      <div className={styles.row}>
        <input
          data-testid={CUSTOMER_INFO_TEST_IDS.inputEmail}
          required={true}
          placeholder={getLabel('email')}
          type="email"
          name="email"
          readOnly={!!initialEmailAddress}
          autoComplete="email"
          value={emailAddressValue}
          className={cx(styles.input, emailInputState.hasError && styles.error)}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailInputState({
              ...emailInputState,
              // As soon as the email is valid consider it touched to
              // enable the button on last keystroke of valid email
              ...{
                isTouched: validateEmail(e.target.value),
                hasError: (!validateEmail(e.target.value) || comparePrevEmail(e.target.value)),
              },
            });
          }}
          onBlur={(e) => {
            setEmailInputState({
              ...emailInputState,
              ...{
                isTouched: true,
                hasError:
                  !validateEmail(e.target.value)
              },
            });
          }}
        />
      </div>
      <div className={cx(styles.row, styles.fullWidth)}>
        {isCustomerInfoFeatureEnabled && isBusinessNameEnabled && (
          <input
            data-testid={CUSTOMER_INFO_TEST_IDS.inputBusinessName}
            className={cx(
              styles.input,
              styles.dualInput,
              businessNameInputState.hasError && styles.error
            )}
            placeholder={getLabel(
              'businessName',
              isBusinessNameRequired,
            )}
            name="businessName"
            type="text"
            value={businessNameValue}
            readOnly={!!initialBusinessName}
            onChange={(e) => {
              setBusinessName(e.target.value);
              setBusinessNameInputState({
                hasError: false,
                isTouched: isBusinessNameRequired,
              });
            }}
            onBlur={(e) => {
              setBusinessNameInputState({
                isTouched: true,
                hasError:
                  isBusinessNameRequired &&
                  !initialBusinessName &&
                  !validateBusinessName(e.target.value),
              });
            }}
          />
        )}
        {isCustomerInfoFeatureEnabled && isPhoneNumberEnabled && (
          <input
            data-testid={CUSTOMER_INFO_TEST_IDS.inputPhoneNumber}
            className={cx(
              styles.input,
              phoneNumberInputState.hasError && styles.error
            )}
            placeholder={getLabel('phoneNumber', isPhoneNumberRequired)}
            name="phoneNumber"
            maxLength={21}
            type="tel"
            value={formatValueAsPhone(phoneValue)}
            readOnly={!!initialPhoneNumber}
            onChange={(e) => {
              setPhoneValue(e.target.value);
              setPhoneNumberInputState({
                isTouched: isPhoneNumberRequired,
                hasError: false,
              });
            }}
            onBlur={(e) => {
              setPhoneNumberInputState({
                isTouched: true,
                hasError:
                  isPhoneNumberRequired &&
                  !initialPhoneNumber &&
                  !validatePhoneNumber(e.target.value),
              });
            }}
          />
        )}
      </div>
      {isCustomerInfoFeatureEnabled && isBillingAddrEnabled && (
        <>
          <div className={styles.row}>
            <input
              data-testid={CUSTOMER_INFO_TEST_IDS.inputStreetAddress1}
              className={cx(
                styles.input,
                styles.fullWidth,
                addressInputState.streetAddress1.hasError && styles.error
              )}
              placeholder={getLabel(
                'streetAddress1',
                isBillingAddrRequired,
              )}
              name="streetAddress1"
              type="text"
              value={address1Value}
              readOnly={!!initialAddress?.address1}
              onChange={(e) => {
                setAddress1Value(e.target.value);
                setAddressInputState({
                  ...addressInputState,
                  streetAddress1: {
                    isTouched: isBillingAddrRequired,
                    hasError: false,
                  },
                });
              }}
              onBlur={(e) => {
                setAddressInputState({
                  ...addressInputState,
                  streetAddress1: {
                    isTouched: true,
                    hasError:
                      isBillingAddrRequired &&
                      !initialAddress?.address1 &&
                      lengthCheckRule(e.target.value, 1, 127),
                  },
                });
              }}
            />
          </div>
        </>
      )}
      {isCustomerInfoFeatureEnabled && isBillingAddrEnabled && (
        <>
          <div className={styles.row}>
            <input
              data-testid={CUSTOMER_INFO_TEST_IDS.inputStreetAddress2}
              className={cx(
                styles.input,
                styles.fullWidth,
                addressInputState.streetAddress2.hasError && styles.error
              )}
              placeholder={getLabel(
                'streetAddress2',
                false
              )}
              name="streetAddress2"
              type="text"
              value={address2Value}
              readOnly={!!initialAddress?.address2}
              onChange={(e) => {
                setAddress2Value(e.target.value);
                setAddressInputState({
                  ...addressInputState,
                  streetAddress2: {
                    isTouched: true,
                    hasError: e.target.value.length > 0 && lengthCheckRule(e.target.value, 0, 127),
                  },
                });
              }}
              onBlur={(e) => {
                setAddressInputState({
                  ...addressInputState,
                  streetAddress2: {
                    isTouched: true,
                    hasError: e.target.value.length > 0 && !initialAddress?.address2 && lengthCheckRule(e.target.value, 0, 127),
                  },
                });
              }}
            />
          </div>
          <div className={styles.row}>
            <input
              data-testid={CUSTOMER_INFO_TEST_IDS.inputCity}
              className={cx(
                styles.input,
                styles.dualInput,
                addressInputState.city.hasError && styles.error
              )}
              placeholder={getLabel('city', isBillingAddrRequired)}
              name="city"
              type="text"
              value={cityValue}
              readOnly={!!initialAddress?.city}
              onChange={(e) => {
                setCity(e.target.value);
                setAddressInputState({
                  ...addressInputState,
                  city: {
                    isTouched: isBillingAddrRequired,
                    hasError: false,
                  },
                });
              }}
              onBlur={(e) => {
                setAddressInputState({
                  ...addressInputState,
                  city: {
                    isTouched: true,
                    hasError:
                      isBillingAddrRequired &&
                      !initialAddress?.city &&
                      lengthCheckRule(e.target.value, 1, 127),
                  },
                });
              }}
            />
            <span tabIndex={1}
              className={cx(styles.input, styles.dualInput, styles.splitInput)}
              onBlur={() => {
                setAddressInputState({
                  ...addressInputState,
                  state: {
                    isTouched: true,
                    hasError:
                      isBillingAddrRequired && stateValue === '',
                  },
                });
              }}
            >
              <Dropdown
                options={STATE_LIST}
                className={cx(addressInputState.state.hasError && styles.error)}
                label={handleLabel}
                onChange={handleStateChange}
                placeHolder={getLabel('state', isBillingAddrRequired)}
                disabled={!!initialAddress?.state}
              />
            </span>
            <input
              data-testid={CUSTOMER_INFO_TEST_IDS.inputZip}
              className={cx(
                styles.input,
                styles.dualInput,
                styles.splitInput,
                styles.marLeft16,
                addressInputState.zip.hasError && styles.error
              )}
              placeholder={getLabel('zip', isBillingAddrRequired)}
              name="zip"
              type="text"
              value={zipValue}
              readOnly={!!initialAddress?.zip}
              onChange={(e) => {
                setZip(e.target.value);
                setAddressInputState({
                  ...addressInputState,
                  zip: {
                    isTouched: isBillingAddrRequired,
                    hasError: false,
                  },
                });
              }}
              onBlur={(e) => {
                setAddressInputState({
                  ...addressInputState,
                  zip: {
                    isTouched: true,
                    hasError:
                      isBillingAddrRequired &&
                      !initialAddress?.zip &&
                      !validateZipCode(e.target.value),
                  },
                });
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export { CustomerInfo };
